/**
 * Author-facing rendering of schema-validation issues (R3 — promoted to
 * library code from `scripts/validate-content.mjs`, which now imports it).
 *
 * @remarks
 * One issue, one line, `path: message` — the wording the future creator
 * studio surfaces verbatim, so it lives in the airlock rather than in a
 * script nobody unit-tests. Structured consumers should read the `ZodError`
 * itself; this is the human channel.
 *
 * @packageDocumentation
 */

import type { z } from 'zod';

/**
 * Render one Zod issue as a single human-actionable line.
 *
 * @param issue - The issue to render
 * @returns `path: message` (or `(document root): message` for top-level issues)
 */
export const describeSchemaIssue = (issue: z.core.$ZodIssue): string => {
	const issuePath = issue.path.length > 0 ? issue.path.map(String).join('.') : '(document root)';

	return `${issuePath}: ${issue.message}`;
};

/**
 * Render every issue in a Zod error, in reported order.
 *
 * @param zodError - The error whose issues to render
 * @returns One line per issue
 */
export const describeSchemaIssues = (zodError: z.ZodError): readonly string[] =>
	zodError.issues.map((issue) => describeSchemaIssue(issue));
